import {
  DATE_ORDER_LABELS,
  type DateOrder,
  OCR_FIELD_LABELS,
  type OcrTemplate,
  type OcrZone,
  type OcrZoneMode,
  PERMISSIONS,
} from '@edms/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Crosshair, Save, ScanLine, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import { PageBody, PageHeader } from '@/components/page-header';
import { useToast } from '@/components/ui/overlays';
import { Button, Checkbox, Field, Input, Select } from '@/components/ui/primitives';
import { Badge, Card, EmptyState, SectionHeader } from '@/components/ui/surfaces';
import { useAuth } from '@/features/auth/auth-context';
import { api, ApiError } from '@/lib/api-client';
import { cn } from '@/lib/utils';

const MODE_LABELS: Record<OcrZoneMode, string> = {
  TEXT: 'Texte',
  NUMBER: 'Numéro',
  DATE: 'Date',
  AMOUNT: 'Montant',
  BARCODE: 'Code-barres',
  QR_CODE: 'QR Code',
  SIGNATURE: 'Signature',
};

/**
 * Configuration des zones OCR (§20.1).
 *
 * Le cahier des charges impose que les zones soient « configurables via une
 * interface d'administration, sans modification du code source ». Cet écran est
 * cette interface : un plan de page à gauche où l'on positionne les rectangles,
 * les paramètres de la zone sélectionnée à droite.
 *
 * La prévisualisation est schématique et assumée comme telle — elle sert à
 * situer les zones les unes par rapport aux autres, pas à les caler au pixel
 * près sur un reçu réel.
 */
export function OcrZonesPage(): React.JSX.Element {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [templateId, setTemplateId] = useState<string | null>(null);
  const [zones, setZones] = useState<OcrZone[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [dirty, setDirty] = useState(false);

  const { data: templates, isLoading } = useQuery({
    queryKey: ['ocr-templates'],
    queryFn: () => api.get<OcrTemplate[]>('/ocr/templates'),
  });

  const template = templates?.find((item) => item.id === templateId) ?? templates?.[0] ?? null;

  useEffect(() => {
    if (template && !dirty) {
      setTemplateId(template.id);
      setZones(template.zones);
      setSelectedIndex(0);
    }
  }, [template, dirty]);

  const save = useMutation({
    mutationFn: () => {
      if (!template) throw new Error('Aucun modèle sélectionné.');
      return api.put<OcrTemplate>(`/ocr/templates/${template.id}`, {
        name: template.name,
        description: template.description,
        branchId: template.branchId,
        isActive: template.isActive,
        zones,
      });
    },
    onSuccess: () => {
      toast.success('Zones enregistrées', 'La nouvelle configuration prend effet immédiatement.');
      setDirty(false);
      void queryClient.invalidateQueries({ queryKey: ['ocr-templates'] });
    },
    onError: (error) =>
      toast.error('Enregistrement refusé', error instanceof ApiError ? error.message : undefined),
  });

  const writable = can(PERMISSIONS.OCR_TEMPLATES_WRITE);
  const selected = zones[selectedIndex];

  const updateZone = (index: number, patch: Partial<OcrZone>): void => {
    setZones((current) =>
      current.map((zone, position) => (position === index ? { ...zone, ...patch } : zone)),
    );
    setDirty(true);
  };

  if (isLoading) {
    return (
      <PageBody>
        <Card>
          <EmptyState icon={ScanLine} title="Chargement des modèles…" />
        </Card>
      </PageBody>
    );
  }

  if (!template) {
    return (
      <PageBody>
        <Card>
          <EmptyState
            icon={ScanLine}
            title="Aucun modèle de zones"
            description="Créez un modèle pour indiquer au moteur OCR où lire les informations sur vos reçus."
          />
        </Card>
      </PageBody>
    );
  }

  return (
    <>
      <PageHeader
        title="Zones OCR"
        description="Emplacements lus par le moteur de reconnaissance, modifiables sans redéploiement"
        actions={
          <>
            {templates && templates.length > 1 && (
              <Select
                size="sm"
                value={template.id}
                onChange={(event) => {
                  setTemplateId(event.target.value);
                  setDirty(false);
                }}
                className="w-64"
                aria-label="Modèle de zones"
              >
                {templates.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                    {item.branchName ? ` — ${item.branchName}` : ' — global'}
                  </option>
                ))}
              </Select>
            )}

            {writable && (
              <Button
                variant="primary"
                icon={<Save className="size-4" />}
                disabled={!dirty}
                loading={save.isPending}
                onClick={() => save.mutate()}
              >
                Enregistrer
              </Button>
            )}
          </>
        }
      />

      <PageBody>
        <div className="grid gap-4 xl:grid-cols-5">
          <Card className="xl:col-span-3">
            <SectionHeader
              title="Plan de la page"
              description="Sélectionnez une zone pour la modifier"
              actions={
                <Badge tone={template.isActive ? 'success' : 'neutral'} dot>
                  version {template.version}
                </Badge>
              }
            />

            <div
              className="relative mt-4 w-full overflow-hidden rounded-md border border-line bg-white"
              style={{ aspectRatio: '1240 / 1754' }}
            >
              {zones.map((zone, index) => (
                <button
                  key={`${zone.fieldKey}-${index}`}
                  type="button"
                  onClick={() => setSelectedIndex(index)}
                  className={cn(
                    'absolute flex items-start justify-start rounded-sm border-2 p-1 text-left transition-colors',
                    index === selectedIndex
                      ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent)/0.16)]'
                      : 'border-[hsl(var(--border-strong))] bg-[hsl(var(--border-strong)/0.08)] hover:bg-[hsl(var(--accent)/0.1)]',
                  )}
                  style={{
                    left: `${zone.rect.x * 100}%`,
                    top: `${zone.rect.y * 100}%`,
                    width: `${zone.rect.width * 100}%`,
                    height: `${zone.rect.height * 100}%`,
                  }}
                  title={zone.label}
                >
                  <span className="truncate rounded bg-[hsl(var(--surface-inverted))] px-1 text-[9px] leading-tight font-medium text-[hsl(var(--text-inverted))]">
                    {zone.label}
                  </span>
                </button>
              ))}
            </div>

            <p className="mt-3 text-2xs text-ink-faint">
              Les coordonnées sont relatives à la page (0 à 1) : elles restent valides quelle que
              soit la résolution de numérisation.
            </p>
          </Card>

          <div className="space-y-4 xl:col-span-2">
            <Card>
              <SectionHeader title="Zones déclarées" description={`${zones.length} zone(s)`} />
              <ul className="mt-3 space-y-1">
                {zones.map((zone, index) => (
                  <li key={`${zone.fieldKey}-${index}`}>
                    <button
                      type="button"
                      onClick={() => setSelectedIndex(index)}
                      className={cn(
                        'flex w-full items-center justify-between gap-2 rounded-md px-2.5 py-2 text-left transition-colors',
                        index === selectedIndex ? 'bg-accent-soft' : 'hover:bg-hover',
                      )}
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-xs font-medium text-ink">
                          {zone.label}
                        </span>
                        <span className="block truncate font-mono text-2xs text-ink-faint">
                          {zone.fieldKey}
                        </span>
                      </span>
                      <span className="flex shrink-0 items-center gap-1">
                        {zone.anchor && <Badge tone="info">étiquette</Badge>}
                        {zone.required && <Badge tone="accent">requis</Badge>}
                        <Badge tone="neutral">{MODE_LABELS[zone.mode]}</Badge>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </Card>

            {selected && (
              <Card>
                <SectionHeader
                  title="Paramètres de la zone"
                  actions={
                    writable &&
                    zones.length > 1 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={<Trash2 className="size-3.5" />}
                        onClick={() => {
                          setZones((current) => current.filter((_, index) => index !== selectedIndex));
                          setSelectedIndex(0);
                          setDirty(true);
                        }}
                      >
                        Retirer
                      </Button>
                    )
                  }
                />

                <fieldset disabled={!writable} className="mt-4 space-y-3">
                  <Field label="Libellé" htmlFor="zone-label">
                    <Input
                      id="zone-label"
                      size="sm"
                      value={selected.label}
                      onChange={(event) => updateZone(selectedIndex, { label: event.target.value })}
                    />
                  </Field>

                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Clé technique" htmlFor="zone-key">
                      <Input
                        id="zone-key"
                        size="sm"
                        value={selected.fieldKey}
                        onChange={(event) =>
                          updateZone(selectedIndex, { fieldKey: event.target.value })
                        }
                        className="font-mono"
                      />
                    </Field>

                    <Field label="Type de donnée" htmlFor="zone-mode">
                      <Select
                        id="zone-mode"
                        size="sm"
                        value={selected.mode}
                        onChange={(event) =>
                          updateZone(selectedIndex, { mode: event.target.value as OcrZoneMode })
                        }
                      >
                        {(Object.keys(MODE_LABELS) as OcrZoneMode[]).map((mode) => (
                          <option key={mode} value={mode}>
                            {MODE_LABELS[mode]}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  </div>

                  <Field
                    label="Étiquette imprimée"
                    htmlFor="zone-anchor"
                    hint="Renseignée, la valeur est cherchée après cette étiquette plutôt qu'à une position fixe. Indispensable si la mise en page varie."
                  >
                    <Input
                      id="zone-anchor"
                      size="sm"
                      value={selected.anchor ?? ''}
                      onChange={(event) =>
                        updateZone(selectedIndex, { anchor: event.target.value || null })
                      }
                      className="font-mono"
                      placeholder="Order No:"
                    />
                  </Field>

                  {selected.mode === 'DATE' && (
                    <Field
                      label="Ordre de la date"
                      htmlFor="zone-date-order"
                      hint="« 08/07/2026 » se lit différemment selon l'émetteur."
                    >
                      <Select
                        id="zone-date-order"
                        size="sm"
                        value={selected.dateOrder}
                        onChange={(event) =>
                          updateZone(selectedIndex, { dateOrder: event.target.value as DateOrder })
                        }
                      >
                        {(Object.keys(DATE_ORDER_LABELS) as DateOrder[]).map((order) => (
                          <option key={order} value={order}>
                            {DATE_ORDER_LABELS[order]}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  )}

                  <div className="grid grid-cols-4 gap-2">
                    {(['x', 'y', 'width', 'height'] as const).map((axis) => (
                      <Field
                        key={axis}
                        label={{ x: 'X', y: 'Y', width: 'Largeur', height: 'Hauteur' }[axis]}
                        htmlFor={`zone-${axis}`}
                      >
                        <Input
                          id={`zone-${axis}`}
                          size="sm"
                          type="number"
                          step="0.01"
                          min="0"
                          max="1"
                          value={selected.rect[axis]}
                          onChange={(event) =>
                            updateZone(selectedIndex, {
                              rect: { ...selected.rect, [axis]: Number(event.target.value) },
                            })
                          }
                        />
                      </Field>
                    ))}
                  </div>

                  <Field
                    label="Caractères autorisés"
                    htmlFor="zone-whitelist"
                    hint="Restreindre l'alphabet augmente nettement la précision d'un numéro."
                  >
                    <Input
                      id="zone-whitelist"
                      size="sm"
                      value={selected.characterWhitelist ?? ''}
                      onChange={(event) =>
                        updateZone(selectedIndex, {
                          characterWhitelist: event.target.value || null,
                        })
                      }
                      className="font-mono"
                      placeholder="0123456789ABCDEF-/"
                    />
                  </Field>

                  <Field
                    label="Motif de validation"
                    htmlFor="zone-pattern"
                    hint="Expression régulière appliquée après normalisation."
                  >
                    <Input
                      id="zone-pattern"
                      size="sm"
                      value={selected.pattern ?? ''}
                      onChange={(event) =>
                        updateZone(selectedIndex, { pattern: event.target.value || null })
                      }
                      className="font-mono"
                      placeholder="^[A-Z0-9-]{4,40}$"
                    />
                  </Field>

                  <Field
                    label="Confiance minimale"
                    htmlFor="zone-confidence"
                    hint="En deçà, le document part en validation manuelle."
                  >
                    <Input
                      id="zone-confidence"
                      size="sm"
                      type="number"
                      step="0.05"
                      min="0"
                      max="1"
                      value={selected.minConfidence}
                      onChange={(event) =>
                        updateZone(selectedIndex, { minConfidence: Number(event.target.value) })
                      }
                    />
                  </Field>

                  <Checkbox
                    label="Champ obligatoire"
                    hint="Son absence envoie le document en validation manuelle."
                    checked={selected.required}
                    onChange={(event) =>
                      updateZone(selectedIndex, { required: event.target.checked })
                    }
                  />

                  <Checkbox
                    label="Valeur sur plusieurs lignes"
                    hint="Pour les raisons sociales qui débordent sur la ligne suivante."
                    checked={selected.multiline}
                    onChange={(event) =>
                      updateZone(selectedIndex, { multiline: event.target.checked })
                    }
                  />
                </fieldset>

                {writable && (
                  <Button
                    className="mt-4"
                    size="sm"
                    fullWidth
                    icon={<Crosshair className="size-3.5" />}
                    onClick={() => {
                      setZones((current) => [
                        ...current,
                        {
                          fieldKey: `zone_${current.length + 1}`,
                          label: `Nouvelle zone ${current.length + 1}`,
                          mode: 'TEXT',
                          pageIndex: 0,
                          rect: { x: 0.1, y: 0.1, width: 0.3, height: 0.06 },
                          languages: ['fra', 'eng'],
                          characterWhitelist: null,
                          pattern: null,
                          anchor: null,
                          dateOrder: 'AUTO',
                          multiline: false,
                          required: false,
                          minConfidence: 0.7,
                          order: current.length,
                        },
                      ]);
                      setSelectedIndex(zones.length);
                      setDirty(true);
                    }}
                  >
                    Ajouter une zone
                  </Button>
                )}
              </Card>
            )}
          </div>
        </div>

        <p className="text-2xs text-ink-faint">
          Champs reconnus par le moteur :{' '}
          {Object.values(OCR_FIELD_LABELS).slice(0, 10).join(', ')}…
        </p>
      </PageBody>
    </>
  );
}
